<template>
  <Modal :value="value" :title="modalTitle" width="760" :mask-closable="false" class-name="recruit-notes-modal"
    @on-visible-change="onVisibleChange">
    <Spin fix v-if="detailLoading"></Spin>
    <p class="notes-sub">
      一、面试笔记 · 二、试讲打分
      <span v-if="scoreStats.hasScored">（总分 {{ scoreStats.sumText }} · 平均分 {{ scoreStats.avgText }}）</span>
      <span class="notes-time" v-if="noteTime">最近保存：{{ noteTime }} {{ noteBy }}</span>
    </p>
    <div class="notes-body" @paste="onPaste">
      <div class="notes-field">
        <div class="notes-label">上课链接</div>
        <div class="class-link-row">
          <Input v-model="form.classLink" placeholder="https:// 粘贴课堂/会议链接" class="class-link-input"></Input>
          <a v-if="classLinkOpenable" class="class-link-open" :href="classLinkHref" target="_blank" rel="noopener noreferrer">打开</a>
        </div>
      </div>

      <div class="notes-field">
        <div class="notes-label">附件</div>
        <Upload
          ref="attachUpload"
          type="drag"
          :action="uploadAction"
          :headers="uploadHeaders"
          :show-upload-list="false"
          :max-size="maxSizeKb"
          :before-upload="beforeUpload"
          :on-success="uploadSuccess"
          :on-error="uploadError"
          :on-exceeded-size="exceededSize"
          multiple>
          <div class="upload-drag-inner">
            <Icon type="ios-cloud-upload" size="40" class="upload-drag-icon"></Icon>
            <p>点击或将文件拖拽到这里上传</p>
            <p class="upload-drag-tip">也可复制文件后在本弹窗内粘贴上传，单文件不超过 100MB</p>
          </div>
        </Upload>
        <ul class="attach-list" v-if="form.attachments.length">
          <li v-for="(file, index) in form.attachments" :key="file.url + index">
            <Icon type="document"></Icon>
            <a :href="file.url" target="_blank" :title="file.name">{{ file.name }}</a>
            <span class="attach-size">{{ formatSize(file.size) }}</span>
            <a class="attach-del" @click="removeAttachment(index)">删除</a>
          </li>
        </ul>
        <p class="notes-empty" v-else>暂无附件</p>
      </div>

      <h3 class="notes-section"><span class="section-no">一</span>面试笔记</h3>
      <div class="notes-panel">
        <div class="notes-field">
          <div class="notes-label">个人情况 <span class="req">*</span></div>
          <Input type="textarea" v-model="form.personalSituation" :autosize="{minRows: 3, maxRows: 6}"
            placeholder="记录个人背景、性格、沟通印象等..."></Input>
        </div>
        <div class="notes-field">
          <div class="notes-label">工作情况 <span class="req">*</span></div>
          <Input type="textarea" v-model="form.workSituation" :autosize="{minRows: 3, maxRows: 6}"
            placeholder="记录工作经历、在职状态、可用性等..."></Input>
        </div>
        <Row :gutter="16">
          <Col :span="12">
            <div class="notes-label">试用期薪资（美元） <span class="req">*</span></div>
            <Input v-model="form.probationSalary" placeholder="例如 12 或 10-14">
              <span slot="prepend">$</span>
            </Input>
          </Col>
          <Col :span="12">
            <div class="notes-label">转正后薪资（美元） <span class="req">*</span></div>
            <Input v-model="form.regularSalary" placeholder="例如 15 或 14-18">
              <span slot="prepend">$</span>
            </Input>
          </Col>
        </Row>
      </div>

      <h3 class="notes-section"><span class="section-no is-green">二</span>试讲打分</h3>
      <Row :gutter="16">
        <Col :span="12">
          <div class="notes-label is-green">优点 <span class="req">*</span></div>
          <Input type="textarea" v-model="form.demoStrengths" :autosize="{minRows: 3, maxRows: 6}"
            placeholder="记录试讲中的亮点和优势..."></Input>
        </Col>
        <Col :span="12">
          <div class="notes-label is-orange">缺点 <span class="req">*</span></div>
          <Input type="textarea" v-model="form.demoWeaknesses" :autosize="{minRows: 3, maxRows: 6}"
            placeholder="记录需要改进的地方..."></Input>
        </Col>
      </Row>
      <div class="score-head">
        <span class="notes-label">评分标准 <span class="req">*</span></span>
        <span class="score-tip">0 未评 · 1 差 · 2 偏弱 · 3 中等及格 · 3.5 · 4 良好 · 4.5 · 5 优秀</span>
      </div>
      <div class="score-sum" v-if="scoreStats.hasScored">
        已打分汇总　满分 45 分　总分 <b>{{ scoreStats.sumText }}</b>　|　平均分 <b>{{ scoreStats.avgText }}</b>
      </div>
      <div class="score-panel">
        <div class="score-row" v-for="c in criteria" :key="c.key">
          <span class="score-label">{{ c.label }}</span>
          <RadioGroup v-model="form.demoScores[c.key]" type="button" size="small">
            <Radio v-for="opt in scoreOptions" :key="opt" :label="opt">{{ opt }}</Radio>
          </RadioGroup>
        </div>
      </div>
      <div class="notes-field">
        <div class="notes-label">备注</div>
        <Input type="textarea" v-model="form.demoRemarks" :autosize="{minRows: 2, maxRows: 5}"
          placeholder="补充说明、后续跟进建议等..."></Input>
      </div>
      <p class="notes-complete" :class="{'is-ok': complete}">
        {{ complete ? '笔记已齐全，可提交复审' : '带 * 的项全部填写且 9 项打分都大于 0 后，才可提交复审' }}
      </p>
      <p class="draft-hint" v-if="draftHint">{{ draftHint }}</p>
    </div>
    <div slot="footer">
      <Button type="text" @click="close">取消</Button>
      <Button type="primary" :loading="saving" :disabled="uploading" @click="save">保存</Button>
    </div>
  </Modal>
</template>
<script>
import { DEMO_SCORE_CRITERIA, DEMO_SCORE_OPTIONS, emptyScores, parseJson, tchName } from '../recruitConst'

var MAX_SIZE_KB = 102400
var MAX_SIZE_BYTES = MAX_SIZE_KB * 1024
var DRAFT_DEBOUNCE_MS = 800

function emptyForm () {
  return {
    classLink: '',
    attachments: [],
    personalSituation: '',
    workSituation: '',
    probationSalary: '',
    regularSalary: '',
    demoStrengths: '',
    demoWeaknesses: '',
    demoScores: emptyScores(),
    demoRemarks: ''
  }
}

function notBlank (v) {
  return v !== null && v !== undefined && String(v).trim() !== ''
}

export default {
  name: 'RecruitNotesModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      form: emptyForm(),
      criteria: DEMO_SCORE_CRITERIA,
      scoreOptions: DEMO_SCORE_OPTIONS,
      maxSizeKb: MAX_SIZE_KB,
      uploadAction: this.$appConfig.baseUrl + '/upload/fileUpload',
      uploadHeaders: {},
      uploading: false,
      uploadingCount: 0,
      detailLoading: false,
      saving: false,
      hasNotes: false,
      noteTime: '',
      noteBy: '',
      draftHint: '',
      draftTimer: null,
      restoringDraft: false
    }
  },
  computed: {
    modalTitle () {
      let name = tchName(this.row)
      return (this.hasNotes ? '查看笔记' : '添加笔记') + (name ? ' - ' + name : '')
    },
    classLinkOpenable () {
      return notBlank(this.form.classLink)
    },
    classLinkHref () {
      let u = (this.form.classLink || '').trim()
      if (!u) {
        return '#'
      }
      if (!/^https?:\/\//i.test(u)) {
        return 'https://' + u
      }
      return u
    },
    scoreStats () {
      let scores = this.form.demoScores || {}
      let vals = DEMO_SCORE_CRITERIA.map(function (c) {
        return Number(scores[c.key]) || 0
      })
      let sum = vals.reduce(function (a, b) {
        return a + b
      }, 0)
      let avg = vals.length ? sum / vals.length : 0
      return {
        hasScored: vals.some(function (v) {
          return v > 0
        }),
        sumText: Number.isInteger(sum) ? String(sum) : sum.toFixed(1),
        avgText: avg.toFixed(1)
      }
    },
    complete () {
      let f = this.form
      if (!notBlank(f.personalSituation) || !notBlank(f.workSituation) ||
        !notBlank(f.probationSalary) || !notBlank(f.regularSalary) ||
        !notBlank(f.demoStrengths) || !notBlank(f.demoWeaknesses)) {
        return false
      }
      let scores = f.demoScores || {}
      return DEMO_SCORE_CRITERIA.every(function (c) {
        return (Number(scores[c.key]) || 0) > 0
      })
    }
  },
  watch: {
    value (val) {
      if (val) {
        this.init()
      }
    },
    form: {
      deep: true,
      handler () {
        if (!this.value || this.detailLoading || this.restoringDraft || this.saving) {
          return
        }
        this.scheduleDraftSave()
      }
    }
  },
  beforeDestroy () {
    if (this.draftTimer) {
      clearTimeout(this.draftTimer)
      this.draftTimer = null
    }
  },
  methods: {
    init () {
      this.form = emptyForm()
      this.uploadHeaders = this.buildUploadHeaders()
      this.uploading = false
      this.uploadingCount = 0
      this.saving = false
      this.draftHint = ''
      this.fillForm(this.row)
      if (!this.row || !this.row.tchId) {
        return
      }
      this.detailLoading = true
      this.$http.post('/tchRecruit/interview/detail', {tchId: this.row.tchId}, {silent: true}).then((res) => {
        if (res && res.status === 'ok' && res.data) {
          this.fillForm(res.data)
        }
      }).catch(() => {}).finally(() => {
        this.detailLoading = false
        this.restoreDraft()
      })
    },
    fillForm (row) {
      if (!row) {
        return
      }
      let attachments = parseJson(row.attachments, [])
      let scores = parseJson(row.demoScores, {})
      this.form = {
        classLink: row.classLink || '',
        attachments: Array.isArray(attachments) ? attachments : [],
        personalSituation: row.personalSituation || '',
        workSituation: row.workSituation || '',
        probationSalary: row.probationSalary || '',
        regularSalary: row.regularSalary || '',
        demoStrengths: row.demoStrengths || '',
        demoWeaknesses: row.demoWeaknesses || '',
        demoScores: Object.assign(emptyScores(), this.normalizeScores(scores)),
        demoRemarks: row.demoRemarks || ''
      }
      this.hasNotes = !!row.hasNotes
      this.noteTime = row.noteTime || ''
      this.noteBy = row.noteBy || ''
    },
    normalizeScores (scores) {
      let out = {}
      if (!scores || typeof scores !== 'object') {
        return out
      }
      DEMO_SCORE_CRITERIA.forEach(function (c) {
        if (scores[c.key] !== undefined && scores[c.key] !== null) {
          out[c.key] = Number(scores[c.key]) || 0
        }
      })
      return out
    },
    draftStorageKey () {
      let tchId = this.row && this.row.tchId
      if (!tchId) {
        return ''
      }
      let account = this.$store.get('account') || 'anon'
      return 'recruit-notes-draft:' + account + ':' + tchId
    },
    scheduleDraftSave () {
      let self = this
      if (this.draftTimer) {
        clearTimeout(this.draftTimer)
      }
      this.draftTimer = setTimeout(function () {
        self.persistDraft()
      }, DRAFT_DEBOUNCE_MS)
    },
    persistDraft () {
      let key = this.draftStorageKey()
      if (!key || !this.value) {
        return
      }
      try {
        localStorage.setItem(key, JSON.stringify({
          savedAt: Date.now(),
          form: this.form
        }))
        this.draftHint = '已自动暂存'
      } catch (e) {}
    },
    restoreDraft () {
      let key = this.draftStorageKey()
      if (!key) {
        return false
      }
      let raw = null
      try {
        raw = localStorage.getItem(key)
      } catch (e) {
        return false
      }
      if (!raw) {
        return false
      }
      let parsed = null
      try {
        parsed = JSON.parse(raw)
      } catch (e) {
        return false
      }
      if (!parsed || !parsed.form || typeof parsed.form !== 'object') {
        return false
      }
      let f = parsed.form
      this.restoringDraft = true
      this.form = {
        classLink: f.classLink || '',
        attachments: Array.isArray(f.attachments) ? f.attachments : [],
        personalSituation: f.personalSituation || '',
        workSituation: f.workSituation || '',
        probationSalary: f.probationSalary || '',
        regularSalary: f.regularSalary || '',
        demoStrengths: f.demoStrengths || '',
        demoWeaknesses: f.demoWeaknesses || '',
        demoScores: Object.assign(emptyScores(), this.normalizeScores(f.demoScores || {})),
        demoRemarks: f.demoRemarks || ''
      }
      this.draftHint = '已恢复未保存草稿'
      let self = this
      this.$nextTick(function () {
        self.restoringDraft = false
      })
      return true
    },
    clearDraft () {
      let key = this.draftStorageKey()
      if (!key) {
        return
      }
      try {
        localStorage.removeItem(key)
      } catch (e) {}
      this.draftHint = ''
    },
    onVisibleChange (val) {
      if (!val) {
        this.close()
      }
    },
    close () {
      if (this.draftTimer) {
        clearTimeout(this.draftTimer)
        this.draftTimer = null
        if (this.value && !this.saving) {
          this.persistDraft()
        }
      }
      this.$emit('input', false)
    },
    buildUploadHeaders () {
      let headers = {token: this.$store.get('token')}
      if (this.$store.get('account') && this.$store.get('password')) {
        headers.account = this.$store.get('account')
        headers.password = this.$store.get('password')
      }
      return headers
    },
    beforeUpload () {
      this.uploadingCount++
      this.uploading = true
      return true
    },
    finishOneUpload () {
      this.uploadingCount = Math.max(0, this.uploadingCount - 1)
      this.uploading = this.uploadingCount > 0
    },
    uploadSuccess (res, file) {
      this.finishOneUpload()
      if (!res || res.status !== 'ok' || !res.data || !res.data.url) {
        this.$Message.error((res && res.msg) || ('「' + file.name + '」上传失败'))
        return
      }
      this.form.attachments.push({
        name: res.data.fileName || file.name || res.data.name,
        url: res.data.url,
        size: res.data.size || file.size || 0
      })
    },
    uploadError (error, file) {
      this.finishOneUpload()
      this.$Message.error('「' + ((file && file.name) || '') + '」上传失败：' + ((error && error.message) || '网络错误'))
    },
    exceededSize (file) {
      this.$Message.error('「' + file.name + '」超过 100MB')
    },
    collectPasteFiles (clipboardData) {
      let files = []
      if (!clipboardData) {
        return files
      }
      if (clipboardData.files && clipboardData.files.length) {
        for (let i = 0; i < clipboardData.files.length; i++) {
          if (clipboardData.files[i]) {
            files.push(clipboardData.files[i])
          }
        }
        return files
      }
      let items = clipboardData.items
      if (!items) {
        return files
      }
      for (let i = 0; i < items.length; i++) {
        let item = items[i]
        if (item && item.kind === 'file') {
          let f = item.getAsFile()
          if (f) {
            files.push(f)
          }
        }
      }
      return files
    },
    onPaste (e) {
      let files = this.collectPasteFiles(e && e.clipboardData)
      if (!files.length) {
        return
      }
      e.preventDefault()
      this.uploadPastedFiles(files)
    },
    uploadPastedFiles (files) {
      let upload = this.$refs.attachUpload
      if (!upload || !files || !files.length) {
        return
      }
      this.uploadHeaders = this.buildUploadHeaders()
      for (let i = 0; i < files.length; i++) {
        let file = files[i]
        if (!file) {
          continue
        }
        if (file.size > MAX_SIZE_BYTES) {
          this.$Message.error('「' + (file.name || '粘贴文件') + '」超过 100MB')
          continue
        }
        if (typeof upload.post === 'function') {
          upload.post(file)
        }
      }
    },
    removeAttachment (index) {
      this.form.attachments.splice(index, 1)
    },
    formatSize (bytes) {
      let n = Number(bytes) || 0
      if (n < 1024) {
        return n + ' B'
      }
      if (n < 1024 * 1024) {
        return (n / 1024).toFixed(1) + ' KB'
      }
      return (n / (1024 * 1024)).toFixed(1) + ' MB'
    },
    save () {
      if (!this.row) {
        return
      }
      let f = this.form
      let params = {
        tchId: this.row.tchId,
        classLink: (f.classLink || '').trim(),
        attachments: JSON.stringify(f.attachments || []),
        personalSituation: f.personalSituation,
        workSituation: f.workSituation,
        probationSalary: f.probationSalary,
        regularSalary: f.regularSalary,
        demoStrengths: f.demoStrengths,
        demoWeaknesses: f.demoWeaknesses,
        demoScores: JSON.stringify(f.demoScores || {}),
        demoRemarks: f.demoRemarks
      }
      this.saving = true
      this.$http.post('/tchRecruit/interview/note-save', params, {silent: true}).then((res) => {
        if (res && res.status === 'ok') {
          this.clearDraft()
          this.$Message.success('已保存笔记')
          this.close()
          this.$emit('on-success')
        } else {
          this.$Message.error((res && res.msg) || '保存失败')
        }
      }).catch(() => {}).finally(() => {
        this.saving = false
      })
    }
  }
}
</script>
<style scoped>
.class-link-row { display: flex; align-items: center; gap: 8px; }
.class-link-input { flex: 1; }
.class-link-open { flex: none; font-size: 13px; white-space: nowrap; }
.notes-sub { margin-bottom: 12px; font-size: 12px; color: #808695; }
.notes-time { float: right; }
.notes-body { max-height: 62vh; overflow-y: auto; padding-right: 6px; }
.notes-field { margin-bottom: 14px; }
.notes-label { margin-bottom: 6px; font-size: 13px; font-weight: 600; color: #303133; }
.notes-label.is-green { color: #19be6b; }
.notes-label.is-orange { color: #ff9900; }
.req { color: #ed3f14; }
.notes-empty { margin-top: 6px; font-size: 12px; color: #9AA3B2; }
.upload-drag-inner { padding: 16px 0; text-align: center; }
.upload-drag-icon { color: #3399ff; }
.upload-drag-tip { margin-top: 4px; font-size: 12px; color: #9AA3B2; }
.attach-list { margin-top: 8px; list-style: none; }
.attach-list li { display: flex; align-items: center; padding: 6px 10px; border: 1px solid #e9eaec; border-radius: 4px; }
.attach-list li + li { margin-top: 6px; }
.attach-list li a { flex: 1; min-width: 0; margin-left: 8px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.attach-size { margin: 0 12px; font-size: 12px; color: #9AA3B2; }
.attach-list li a.attach-del { flex: none; margin-left: 0; color: #ed3f14; }
.notes-section { margin: 18px 0 10px; font-size: 14px; font-weight: 600; color: #1c2438; }
.section-no { display: inline-block; width: 20px; height: 20px; margin-right: 8px; border-radius: 50%; background: #2d8cf0; color: #fff; font-size: 12px; line-height: 20px; text-align: center; }
.section-no.is-green { background: #19be6b; }
.notes-panel { padding: 14px; border: 1px solid #e9eaec; border-radius: 6px; background: #f8f8f9; }
.score-head { display: flex; align-items: baseline; justify-content: space-between; margin-top: 14px; }
.score-tip { font-size: 12px; color: #9AA3B2; }
.score-sum { margin: 6px 0; padding: 6px 10px; border: 1px solid #d6e9ff; border-radius: 4px; background: #f0f7ff; font-size: 13px; }
.score-sum b { color: #2d8cf0; }
.score-panel { margin: 6px 0 14px; padding: 10px 12px; border: 1px solid #e9eaec; border-radius: 6px; background: #f8f8f9; }
.score-row { display: flex; align-items: center; padding: 4px 0; }
.score-label { width: 120px; flex: none; font-size: 13px; color: #303133; }
.notes-complete { font-size: 12px; color: #ff9900; }
.notes-complete.is-ok { color: #19be6b; }
.draft-hint { margin-top: 6px; font-size: 12px; color: #2d8cf0; }
</style>



// WEBPACK FOOTER //
// RecruitNotesModal.vue?6ac8f298